// src/components/auth/InputPhoneSADC.jsx
import React from "react";
import {
  AFRIMED_MARKETS,
  AFRICA_MARKETS,
  DIASPORA_MARKETS,
  MARKET_GROUP_LABELS,
  SADC_MARKETS,
  getMarketByDialCode,
} from "@/config/markets.js";

// Serve flags directly from /public (e.g. /za.svg, /gb.svg)
const flagSrc = (isoOrPath) => {
  if (!isoOrPath) return "/za.svg";
  // accept "/za.svg"
  if (isoOrPath.startsWith("/")) return isoOrPath;
  // accept "za" / "ZA"
  return `/${isoOrPath.toLowerCase().replace(/[^a-z]/g, "")}.svg`;
};

/**
 * Phone input for SADC + selected diaspora (visuals unchanged).
 * - Keeps ONLY the allowed markets (grouped, alphabetically)
 * - Prevents dropdown from closing while interacting
 * - Local number is digits-only.
 *
 * Props:
 *  - value      : local digits (string)
 *  - onChange   : (digits) => void
 *  - cc         : controlled country code (e.g. "+27")
 *  - onChangeCc : setter for cc
 *  - className  : passthrough
 *  - countryCodeLabel / numberLabel : optional segment labels for composed forms
 */
const ALL = [
  { kind: "header", title: MARKET_GROUP_LABELS.sadc },
  ...SADC_MARKETS,
  { kind: "header", title: MARKET_GROUP_LABELS.africa },
  ...AFRICA_MARKETS,
  { kind: "header", title: MARKET_GROUP_LABELS.diaspora },
  ...DIASPORA_MARKETS,
];

export function getPhoneMarketByIso(iso) {
  const normalized = String(iso || "").trim().toLowerCase();
  return AFRIMED_MARKETS.find((market) => market.iso === normalized) || null;
}

export function getPhoneMarketFromE164(value) {
  const phone = String(value || "").trim().replace(/[\s()-]/g, "");
  if (!/^\+[1-9]\d{5,14}$/.test(phone)) return null;
  return [...AFRIMED_MARKETS]
    .sort((left, right) => right.dialCode.length - left.dialCode.length)
    .find((market) => phone.startsWith(market.dialCode)) || null;
}

export default function InputPhoneSADC({
  value = "",
  onChange,
  cc,
  onChangeCc,
  countryIso = "",
  onChangeCountry,
  tone = "auth",
  className = "",
  countryCodeLabel = "",
  numberLabel = "",
  leadingIcon = "",
  ariaDescribedBy,
  disabled = false,
  readOnly = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedIso, setSelectedIso] = React.useState("");
  const rootRef = React.useRef(null);
  const countryTriggerRef = React.useRef(null);
  const isSurfaceTone = tone === "surface";
  const interactionDisabled = disabled || readOnly;

  // derive current country by cc
  const current =
    ((countryIso || selectedIso) &&
      AFRIMED_MARKETS.find(
        (market) => market.iso === (countryIso || selectedIso).toLowerCase() && market.dialCode === cc
      )) ||
    (cc === "+1" && AFRIMED_MARKETS.find((market) => market.iso === "us")) ||
    getMarketByDialCode(cc) ||
    AFRIMED_MARKETS.find((market) => market.dialCode === "+27");

  function handleDigits(e) {
    if (interactionDisabled) return;
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 14);
    onChange?.(digits);
  }

  function pickCountry(item) {
    if (interactionDisabled) return;
    if (item?.iso) setSelectedIso(item.iso);
    if (item?.iso) onChangeCountry?.(item.iso.toUpperCase(), item);
    if (item?.dialCode && onChangeCc) onChangeCc(item.dialCode);
    setOpen(false);
    requestAnimationFrame(() => countryTriggerRef.current?.focus());
  }

  // Close menu on escape or outside click
  React.useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        countryTriggerRef.current?.focus();
      }
    }
    function onKeyDown(e) {
      if (!open || e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      countryTriggerRef.current?.focus();
    }
    if (open) {
      document.addEventListener("pointerdown", onDoc);
      document.addEventListener("keydown", onKeyDown);
    }
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (interactionDisabled) setOpen(false);
  }, [interactionDisabled]);

  return (
    <div ref={rootRef} data-afrimed-phone-root="true" className={`relative w-full ${className}`}>
      {/* Field wrapper (56px height, soft border) */}
      <div
        data-afrimed-phone-field="true"
        className="relative h-14 min-h-14 max-h-14 rounded-[12px] flex items-center overflow-hidden"
        style={{
          background: isSurfaceTone
            ? "rgb(var(--clr-surface-cont-highest))"
            : "rgb(var(--afrimed-auth-input-surface))",
          boxShadow: isSurfaceTone
            ? "0 0 0 1px rgba(var(--clr-outline-variant), 0.95) inset"
            : "0 0 0 1px rgba(var(--afrimed-auth-input-border), 0.95) inset",
        }}
      >
        {/* CC picker */}
        <button
          ref={countryTriggerRef}
          type="button"
          onClick={interactionDisabled ? undefined : () => setOpen((v) => !v)}
          disabled={interactionDisabled}
          className={`relative h-full min-h-full max-h-full px-3 flex gap-2 border-r ${countryCodeLabel ? "min-w-[132px] items-center pt-4" : "items-center"}`}
          style={{
            borderColor: isSurfaceTone
              ? "rgba(var(--clr-outline-variant), 0.95)"
              : "rgba(var(--afrimed-auth-input-border), 0.95)",
            color: "rgb(var(--clr-on-surface))",
          }}
          aria-label="Select country code"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {countryCodeLabel ? (
            <span
              className="pointer-events-none absolute left-3 top-2 whitespace-nowrap text-label-medium"
              style={{ color: "rgb(var(--clr-on-surface-variant))" }}
            >
              {countryCodeLabel}
            </span>
          ) : null}
          {/* real flag */}
          <img
            alt=""
            width={16}
            height={12}
            draggable="false"
            src={flagSrc(current?.flag || current?.iso)}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = flagSrc("za");
            }}
          />
          <span className="text-body-medium" style={{ color: "rgb(var(--clr-on-surface))" }}>
            {current?.dialCode}
          </span>
          {!readOnly ? (
            <img
              src="/icons/pharmacy/drop-down-chevron.svg"
              alt=""
              aria-hidden="true"
              className="h-[14px] w-[14px] shrink-0"
            />
          ) : null}
        </button>

        {/* local number (digits only) */}
        <div className="relative flex-1 h-full min-h-full max-h-full min-w-0">
          {numberLabel ? (
            <span
              className="pointer-events-none absolute left-4 top-2 text-label-medium"
              style={{ color: "rgb(var(--clr-on-surface-variant))" }}
            >
              {numberLabel}
            </span>
          ) : null}
          {leadingIcon ? <img src={leadingIcon} alt="" aria-hidden="true" className="pointer-events-none absolute left-3 bottom-3 h-[18px] w-[18px] object-contain" /> : null}
          <input
            inputMode="numeric"
            autoComplete="tel"
            placeholder="71 234 5674"
            value={value}
            onChange={handleDigits}
            aria-label={numberLabel || "Phone number"}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            readOnly={readOnly}
            className={`w-full h-full min-h-full max-h-full px-4 bg-transparent outline-none text-body-large text-on-surface placeholder:text-on-surface-var ${numberLabel ? "pt-4" : ""} ${leadingIcon ? "pl-10" : ""}`}
            style={{ color: "rgb(var(--clr-on-surface))" }}
          />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          role="listbox"
          className="absolute z-[95] top-[calc(100%+8px)] right-0 left-0 w-full max-h-72 overflow-auto rounded-xl border shadow-xl"
          style={{
            background: isSurfaceTone
              ? "rgb(var(--clr-surface-cont-highest))"
              : "rgb(var(--afrimed-auth-input-surface))",
            borderColor: isSurfaceTone
              ? "rgba(var(--clr-outline-variant), 0.95)"
              : "rgba(var(--afrimed-auth-input-border), 0.95)",
          }}
        >
          {ALL.map((item, idx) =>
            item.kind === "header" ? (
              <div
                key={`header-${item.title}-${idx}`}
                className="px-4 py-2 text-label-medium bg-[rgba(24,50,119,0.04)]"
                style={{ color: "rgb(var(--clr-on-surface-variant))" }}
              >
                {item.title}
              </div>
            ) : (
              <button
                key={`${item.iso}-${item.dialCode}`}
                type="button"
                onClick={() => pickCountry(item)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[rgba(24,50,119,0.06)]"
              >
                <img
                  alt=""
                  width={16}
                  height={12}
                  draggable="false"
                  src={flagSrc(item.flag || item.iso)}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = flagSrc("za");
                  }}
                />
                <span
                  className="flex-1 text-body-medium"
                  style={{ color: "rgb(var(--clr-on-surface))" }}
                >
                  {item.name}
                </span>
                <span
                  className="text-body-medium"
                  style={{ color: "rgb(var(--clr-on-surface-variant))" }}
                >
                  {item.dialCode}
                </span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
